import React from "react";
import { valueToString } from "helpers/Helpers";
import classNames from "classnames";

import BaseReportTable from "./BaseReportTable";

import "components/reportTable/reportTable.scss";
import { formatReportCurrency } from "helpers/reportFormat";

interface IProps {
  data: Array<Record<string, any>>;
  columns: Array<Record<string, any>>;
  description: string;
  compileDate: string;
}

type ReportColumnMeta = { id?: string; display_type?: string; type?: string };

export default function RevPARTable(props: IProps) {
  function formatDisplay(type: string, value: string) {
    let newValue = value !== null && value !== undefined ? value : "";

    if (!isNaN(Number(newValue)) && newValue !== "") {
      switch (type) {
        case "currency":
          newValue = formatReportCurrency(value);
          break;
        case "decimal":
          newValue = Number(value).toFixed(2);
          break;
        case "percentage":
          newValue = `${(Number(value) * 100).toFixed(2)}%`;
          break;
        default:
          break;
      }
    } else if (type === "string" && newValue !== "") {
      newValue = valueToString(value).replace(/(^\w|\s\w)/g, c => c.toUpperCase());
    }

    return newValue;
  }

  function cellData(columnId: string, cellValue: string, displayType: string) {
    if (columnId === "date") {
      return cellValue ?? "";
    }

    return formatDisplay(displayType, cellValue);
  }

  function cellOnClick() {
    return {};
  }

  function columnAlignClassNames(column?: ReportColumnMeta | null) {
    const columnId = column?.id;
    const displayType = column?.display_type ?? column?.type;
    const numericTypes = new Set(["currency", "decimal", "percentage", "integer", "number"]);
    const numericColumnIds = new Set([
      "rounds_played",
      "rounds_booked",
      "available_rounds",
      "revenue",
      "revpar",
      "revenue_per_booked",
      "revenue_per_played",
      "occupancy",
    ]);

    const isNumeric =
      columnId !== "date" &&
      ((displayType != null && numericTypes.has(displayType)) ||
        (columnId != null && numericColumnIds.has(columnId)) ||
        (columnId != null && columnId !== "date" && displayType !== "string"));

    return classNames({
      "is-numeric": isNumeric,
      "report-table-cell-currency": displayType === "currency",
    });
  }

  function cellClassNames(cell: { column?: ReportColumnMeta }, isRowLocked: boolean) {
    return classNames(columnAlignClassNames(cell?.column), {
      "report-table-row-locked": isRowLocked,
    });
  }

  /** Applied to thead/tfoot tds — same numeric detection as body cells. */
  function headerClassNames(column: ReportColumnMeta) {
    return columnAlignClassNames(column);
  }

  function footerData(columnId: string, footerRow?: Record<string, any>) {
    if (!footerRow) {
      return null;
    }

    switch (columnId) {
      case "date":
        return footerRow?.date as string;
      case "occupancy":
        return formatDisplay("percentage", String(footerRow[columnId] ?? 0));
      case "revenue":
      case "revpar":
      case "revenue_per_booked":
      case "revenue_per_played":
        return formatDisplay("currency", String(footerRow[columnId] ?? 0));
      default:
        return (footerRow[columnId] as string) ?? null;
    }
  }

  return (
    <BaseReportTable
      data={props.data}
      columns={props.columns}
      description={props.description}
      compileDate={props.compileDate}
      cellOnClick={cellOnClick}
      cellData={cellData}
      cellClassNames={cellClassNames}
      headerClassNames={headerClassNames}
      footerData={footerData}
      expanding={false}
    />
  );
}
